import type { ReactNode } from "react";
import { todayShort } from "@/lib/format";

export interface CompletionStat {
  label: string;
  value: ReactNode;
  dot: "moss" | "amber" | "red" | "indigo" | "empty";
}

export interface CompletionAction {
  label: string;
  onClick: () => void;
}

interface CompletionScreenProps {
  title: ReactNode;
  subtitle?: ReactNode;
  stats?: CompletionStat[];
  primaryAction?: CompletionAction;
  secondaryAction?: CompletionAction;
}

/**
 * Shared "end of a learning session" panel. Used by all four learning
 * modes and by the SRS review screen.
 */
export function CompletionScreen({
  title,
  subtitle,
  stats,
  primaryAction,
  secondaryAction,
}: CompletionScreenProps) {
  return (
    <div className="completion">
      <div className="completion__stamp">
        <div className="completion__stamp-inner">
          <div className="t-mono completion__stamp-top">COMPLETED</div>
          <div className="t-mono completion__stamp-date">{todayShort()}</div>
        </div>
      </div>

      <div className="completion__body">
        <div className="t-eyebrow" style={{ marginBottom: 8 }}>Complete</div>
        <h2 className="t-display" style={{ fontSize: "var(--t-3xl)", margin: 0, letterSpacing: "-0.02em" }}>
          {title}
        </h2>
        {subtitle && (
          <p className="t-display italic" style={{ fontSize: "var(--t-md)", color: "var(--text-dim)", marginTop: 8 }}>
            {subtitle}
          </p>
        )}

        {stats && stats.length > 0 && (
          <div className="completion__stats">
            {stats.map((s, i) => (
              <div key={i} className="completion__stat">
                <div className="row-c" style={{ gap: 6 }}>
                  <span className={`dot dot--${s.dot}`} />
                  <span className="t-meta" style={{ fontSize: 10 }}>{s.label}</span>
                </div>
                <div className="t-display t-num completion__stat-value">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="row-c completion__actions">
          {secondaryAction && (
            <button className="btn" onClick={secondaryAction.onClick} type="button">
              {secondaryAction.label}
            </button>
          )}
          {primaryAction && (
            <button className="btn btn-solid" onClick={primaryAction.onClick} type="button">
              {primaryAction.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
